import { App, Button, Form, Modal } from 'antd';
import { useState } from 'react';
import { useTranslation } from 'translations/hooks';

import { ringSyncService } from 'technical/db/sync/ring-sync.service';
import { RingInterval } from 'ui/quality-control/board/types';

import { SetComplianceForm } from './set-compliance-form';
import { RingRange, SetComplianceFormValues } from './types';

export const SetComplianceModal = ({
  ringInterval,
  isModalVisible,
  closeModal,
}: {
  ringInterval: RingInterval;
  isModalVisible: boolean;
  closeModal: () => void;
}) => {
  const { t } = useTranslation();
  const { message } = App.useApp();
  const [form] = Form.useForm<SetComplianceFormValues>();
  const [isLoading, setIsLoading] = useState(false);

  const save = async (row: SetComplianceFormValues) => {
    setIsLoading(true);
    let isUpdated = false;

    if (row.ringRange === RingRange.Interval) {
      // verify the custom interval is defined
      if (
        !row.ringNumberRange ||
        !row.ringNumberRange[0] ||
        !row.ringNumberRange[1]
      ) {
        form.setFields([
          {
            name: 'ringNumberRange',
            errors: [
              t(
                `qualityControl.recordSegmentDefect.complianceModal.error.wrongInterval`,
              ),
            ],
          },
        ]);

        setIsLoading(false);
        return;
      }

      // verify the custom interval is inside the board loaded
      if (
        row.ringNumberRange[0] < ringInterval.start ||
        row.ringNumberRange[1] > ringInterval.end
      ) {
        form.setFields([
          {
            name: 'ringNumberRange',
            errors: [
              t(
                `qualityControl.recordSegmentDefect.complianceModal.error.tooHigh`,
                {
                  start: ringInterval.start,
                  end: ringInterval.end,
                },
              ),
            ],
          },
        ]);

        setIsLoading(false);
        return;
      }

      isUpdated = await ringSyncService.bulkCompliance(
        row.ringNumberRange[0],
        row.ringNumberRange[1],
      );
    } else {
      isUpdated = await ringSyncService.bulkCompliance(
        ringInterval.start,
        ringInterval.end,
      );
    }

    if (isUpdated) {
      message.open({
        type: 'success',
        content: t(
          'qualityControl.recordSegmentDefect.complianceModal.success',
        ),
      });
    } else {
      message.open({
        type: 'warning',
        content: t(
          'qualityControl.recordSegmentDefect.complianceModal.warning',
        ),
      });
    }

    setIsLoading(false);
    closeModal();
  };

  return (
    <Modal
      title={t('qualityControl.recordSegmentDefect.complianceModal.title')}
      open={isModalVisible}
      onCancel={closeModal}
      destroyOnHidden
      footer={
        <Button type="primary" onClick={form.submit} loading={isLoading}>
          {t('qualityControl.recordSegmentDefect.complianceModal.cta')}
        </Button>
      }
    >
      <p>
        {t('qualityControl.recordSegmentDefect.complianceModal.description')}
      </p>
      {isModalVisible ? <SetComplianceForm form={form} save={save} /> : null}
    </Modal>
  );
};
